import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchUsernameStyle,
  fetchUsernameStyles,
  createUsernameStyle,
  updateUsernameStyle,
  reorderUsernameStyles
} from "src/actions/mine_craft/usernameStylesActions";

export interface UsernameStyleInterface {
  url: string;

  id: number;
  app_id: number;

  order: number;
  coins: number;

  text_gradient_angle: number;
  stroke_gradient_angle: number;
  text_color_start: string;
  text_color_end: string;
  stroke_color_start: string;
  stroke_color_end: string;
  stroke_width: number;

  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null

  newParams: UsernameStyleInterface | undefined;
  newError: string | null;

  newGroupParams: UsernameStyleInterface | undefined;
  newGroupError: string | null;

  reorderIds: number[] | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  newGroupParams: undefined,
  newGroupError: null,

  reorderIds: null,
};

const adaptor = createEntityAdapter<UsernameStyleInterface>();

const slice = createSlice({
  name: 'usernameStyles',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      const defaults = {}

      state.newParams = action.payload ? { ...defaults, ...action.payload } : undefined;
      state.newError  = initialState.newError;
    },
    setNewGroupParams: (state, action) => {
      state.newGroupParams = action.payload;
      state.newGroupError  = initialState.newGroupError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setReorderIds: (state, action) => {
      state.reorderIds = action.payload.ids;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchUsernameStyles.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchUsernameStyles.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.usernameStyles;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchUsernameStyles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #create
      .addCase(createUsernameStyle.fulfilled, (state, action) => {
        const usernameStyle = Object.values(action.payload.usernameStyle)[0];
        state.entities[usernameStyle.id] = usernameStyle;
      })
      .addCase(createUsernameStyle.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchUsernameStyle.fulfilled, (state, action) => {
        const usernameStyle = Object.values(action.payload.usernameStyle)[0];
        state.entities[usernameStyle.id] = usernameStyle;
      })

      // #update
      .addCase(updateUsernameStyle.fulfilled, (state, action) => {
        const usernameStyle = Object.values(action.payload.usernameStyle)[0];
        state.entities[usernameStyle.id] = usernameStyle;
      })
      .addCase(updateUsernameStyle.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #reorder
      .addCase(reorderUsernameStyles.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewGroupParams,
  setNewError,
  setEditError,
  setReorderIds
} = slice.actions;

export const {
  selectById: selectUsernameStyleById,
  selectIds: selectUsernameStyleIds,
  selectEntities: selectUsernameStyleEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.usernameStyles);
